import { useQuery } from "@tanstack/react-query";
import { MapPin, Package, Heart, RefreshCw, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AppHeader } from "@/components/AppHeader";
import { ListingCard } from "@/components/ListingCard";
import { getListings, CURRENT_USER } from "@/data/listings";

export default function Profile() {
  const { data: listings = [] } = useQuery({
    queryKey: ["listings"],
    queryFn: getListings,
  });

  const myListings = listings.filter(l => l.sellerId === CURRENT_USER.id);
  const savedListings = listings.filter(l => l.savedBy.includes(CURRENT_USER.id));

  return (
    <div className="min-h-screen pb-20 md:pb-8">
      <AppHeader />

      <div className="max-w-4xl mx-auto px-4">
        {/* Profile header */}
        <div
          className="mt-6 rounded-3xl p-6 mb-6 relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, var(--blush) 0%, var(--nude) 100%)" }}
        >
          <div className="flex items-start gap-4">
            <div className="relative">
              <img
                src={CURRENT_USER.avatar}
                alt={CURRENT_USER.name}
                className="w-20 h-20 rounded-2xl object-cover border-4 border-white shadow-md"
              />
              <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-green-400 border-2 border-white" />
            </div>
            <div className="flex-1">
              <h1 className="text-xl font-bold">{CURRENT_USER.name}</h1>
              <div className="flex items-center gap-1 text-sm text-muted-foreground mt-0.5 mb-3">
                <MapPin size={12} />
                <span>{CURRENT_USER.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="rounded-full gap-1 text-xs">
                  <Star size={10} fill="currentColor" /> Verified Seller
                </Badge>
                <Badge variant="outline" className="rounded-full text-xs border-border/60">
                  Member since 2024
                </Badge>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mt-5">
            {[
              { icon: Package, label: "Listed", value: myListings.length },
              { icon: Heart, label: "Saved", value: savedListings.length },
              { icon: RefreshCw, label: "Loop events", value: myListings.reduce((s, l) => s + l.wornCount, 0) },
            ].map(({ label, value }) => (
              <div key={label} className="text-center p-3 rounded-2xl bg-white/60 backdrop-blur-sm">
                <p className="text-xl font-bold">{value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Resale Impact */}
        <div
          className="p-4 rounded-2xl mb-6 flex items-center gap-4"
          style={{ background: "var(--rose-light)", border: "1px solid var(--blush)" }}
        >
          <div className="text-3xl">♻️</div>
          <div>
            <p className="font-semibold text-sm" style={{ color: "var(--rose)" }}>Your Resale Impact</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Your wardrobe has completed <strong>{myListings.length} resale loop{myListings.length !== 1 ? "s" : ""}</strong> — keeping fashion circular!
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="listings">
          <TabsList className="w-full rounded-full mb-5">
            <TabsTrigger value="listings" className="flex-1 rounded-full text-sm">
              My Listings ({myListings.length})
            </TabsTrigger>
            <TabsTrigger value="saved" className="flex-1 rounded-full text-sm">
              Saved ({savedListings.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="listings">
            {myListings.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-4xl mb-3">👗</div>
                <h3 className="font-semibold mb-2">No listings yet</h3>
                <p className="text-sm text-muted-foreground mb-5">List your first dress and start the loop!</p>
                <Link to="/sell">
                  <Button className="rounded-full" style={{ background: "var(--rose)", color: "white" }}>
                    Sell a Dress
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
                {myListings.map(l => <ListingCard key={l.id} listing={l} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="saved">
            {savedListings.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-4xl mb-3">❤️</div>
                <h3 className="font-semibold mb-2">No saved dresses</h3>
                <p className="text-sm text-muted-foreground mb-5">Tap the heart on any listing to save it here</p>
                <Link to="/">
                  <Button className="rounded-full" style={{ background: "var(--rose)", color: "white" }}>
                    Discover Dresses
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
                {savedListings.map(l => <ListingCard key={l.id} listing={l} />)}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
